/**
 * EL CONTADOR DE REUNIONES AGENDADAS — cuánto trabajo hizo el bot hoy.
 *
 * EL PREDICADO ES MIXTO, Y ESTÁ ESCRITO PARA QUE NADIE LO LEA MAL
 * ──────────────────────────────────────────────────────────────
 * Cuenta las reuniones **creadas hoy** que **están confirmadas ahora**. Las dos
 * mitades se miden en momentos distintos y eso es deliberado:
 *
 *   · "creada hoy"      → sobre `createdAt`, un instante fijo que no cambia
 *   · "confirmada ahora" → sobre `status`, que puede cambiar después
 *
 * NO es "confirmadas hoy". Una cita creada ayer y confirmada hoy no entra; una
 * creada hoy y cancelada dentro de un rato deja de contar y **el número baja**.
 *
 * Que baje es aceptable, y el dato lo respalda: de las canceladas que se
 * midieron, todas se cancelaron **el mismo día en que se crearon**. Son
 * correcciones del propio flujo, no bajas reales de un cliente que se arrepintió
 * una semana después.
 *
 * POR QUÉ `createdAt` Y NO `date` (la fecha de la cita)
 * ────────────────────────────────────────────────────
 * Medido sobre las 53 reservas reales: **sólo 4 son para el mismo día en que se
 * crearon (8%)**. En el 92% de los casos "creadas hoy" y "para hoy" son
 * conjuntos distintos, y hay días enteros donde uno es cero y el otro no:
 *
 *     2026-07-28 → 5 creadas, 0 para ese día
 *     2026-08-05 → 0 creadas, 6 para ese día
 *
 * El 28 de julio el bot agendó cinco reuniones. Un contador por fecha de la cita
 * habría mostrado **0** justo el día de más trabajo. La barra de estado cuenta
 * actividad del bot —está al lado de MENSAJES RESPONDIDOS—, así que mide lo que
 * el bot hizo, no quién viene hoy.
 */

import { diaEnZona } from './ventasResumen';

/**
 * La zona del negocio, con la MISMA precedencia que `zonaDelTenant` en el
 * agregador de métricas — para que las dos superficies de la misma pantalla no
 * tengan dos "hoy" distintos.
 *
 * La rama de `ecommerce.timeZone` que sí tiene el backend no se replica acá a
 * propósito: una tienda no define en qué día cae una reunión. Si algún día
 * existe una zona propia de agenda, entra como primera rama sin tocar nada más.
 */
export const ZONA_POR_DEFECTO = 'America/Santiago';

export function zonaDelNegocio(config) {
  return config?.calendar?.timeZone || ZONA_POR_DEFECTO;
}

/** El instante de un `createdAt`, venga como Timestamp de Firestore o como fecha. */
function instanteDe(valor) {
  if (!valor) return null;
  const d = valor.toDate ? valor.toDate() : new Date(valor);
  return Number.isFinite(d?.getTime?.()) ? d : null;
}

/**
 * ¿Hay al menos un bot con agenda conectada?
 *
 * El predicado es la presencia de `calendar.provider`, que es exactamente el que
 * ya usa `CalendarIntegration` (`const isConnected = config?.calendar?.provider`)
 * y el mismo que usa el gate de e-commerce con `ecommerce.provider`. Tres
 * lugares, una definición.
 *
 * `configs` es un mapa `botId -> config`, con `undefined` para los que todavía
 * no respondieron. Devuelve `null` mientras falte alguno: la barra no debe
 * afirmar que no hay agenda mientras está averiguando.
 */
export function hayAgendaConectada(configs, botIds) {
  if (!Array.isArray(botIds) || botIds.length === 0) return false;
  const faltantes = botIds.filter((id) => configs?.[id] === undefined);
  if (faltantes.length > 0) return null;
  return botIds.some((id) => !!configs[id]?.calendar?.provider);
}

/**
 * Cuántas reuniones creó el bot hoy y siguen confirmadas.
 *
 * El día se decide con `diaEnZona`, la MISMA función que usa la tarjeta de
 * ventas. No es una comodidad: si esta barra usara la medianoche del navegador
 * —como ya hace CONVERSACIONES HOY— habría dos números uno al lado del otro
 * contando desde medianoches distintas.
 */
export function reunionesCreadasHoy(bookings, hoy, zona) {
  return (bookings || []).filter((b) => {
    if (b?.status !== 'confirmed') return false;
    const creado = instanteDe(b.createdAt);
    return creado !== null && diaEnZona(creado, zona) === hoy;
  }).length;
}
